import {inject} from '@angular/core';
import {ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot} from '@angular/router';
import {StoreDbService} from '../db/store.db.service';
import {MenuService} from '../services/menu.service';
import {ScreenLockService} from '../services/screen-lock.service';
import {TipoPuntoVentaEnum} from '../../shared/enums/tipo-punto-venta-enum';
import {AppMenuItem} from '../../shared/interfaces/menu-config-interface';

function normalizeRoute(url: string): string {
  return url.split(/[?#]/, 1)[0].replace(/^\//, '').replace(/\/$/, '');
}

function belongsToRoute(route: string, menuRoute: string): boolean {
  return route === menuRoute || route.startsWith(`${menuRoute}/`);
}

function isInternalMenuNavigation(
  items: AppMenuItem[],
  currentRoute: string,
  destinationRoute: string
): boolean {
  for (const item of items) {
    if (
      item.visible !== false &&
      item.route &&
      belongsToRoute(currentRoute, item.route) &&
      belongsToRoute(destinationRoute, item.route)
    ) {
      return true;
    }

    if (
      item.children?.length &&
      isInternalMenuNavigation(item.children, currentRoute, destinationRoute)
    ) {
      return true;
    }
  }

  return false;
}

function getDestinationProcess(
  state: RouterStateSnapshot,
  fallbackRoute: ActivatedRouteSnapshot
): string | undefined {
  let current: ActivatedRouteSnapshot | null = state.root;
  let proceso: string | undefined;

  while (current) {
    proceso = current.data?.['proceso'] as string | undefined ?? proceso;
    current = current.firstChild;
  }

  return proceso ?? fallbackRoute.data?.['proceso'] as string | undefined;
}

async function checkScreenLock(
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
  router: Router,
  lock: ScreenLockService,
  menuService: MenuService,
  storeDbService: StoreDbService
): Promise<boolean> {
  const navigation = router.getCurrentNavigation();
  if (navigation?.extras.state?.['skipScreenLock'] === true) return true;

  await menuService.ready();

  const currentRoute = normalizeRoute(router.url);
  const destinationRoute = normalizeRoute(state.url);

  if (
    isInternalMenuNavigation(
      menuService.getRawMenu(),
      currentRoute,
      destinationRoute
    )
  ) {
    return true;
  }

  const menuItem = menuService.getMenuByRoute(destinationRoute);
  const proceso = menuItem?.proceso ?? getDestinationProcess(state, route);
  const requiereAuth = route.data?.['auth'] ?? true;

  if (!proceso) {
    console.warn('Ruta sin proceso definido');
    return true;
  }

  if (!requiereAuth) return true;

  const store = await storeDbService.getConfig();
  if (!store) return true;

  // Tiendas y franquicias comparten el equipo del mostrador. Los modos
  // corporativos y los demas tipos de punto de venta no requieren el bloqueo.
  const isSharedStore =
    store.tipoPV === TipoPuntoVentaEnum.PuntoVentaPropio ||
    store.tipoPV === TipoPuntoVentaEnum.Franquicias;

  if (!isSharedStore) return true;

  // Permite solamente el reintento de la navegacion que acaba de autorizarse.
  if (lock.isAuthorized(state.url, proceso)) return true;

  lock.requestLock(state.url, proceso);
  return false;
}

export const screenLockGuard: CanActivateFn = (route, state) => {
  const router = inject(Router);
  const lock = inject(ScreenLockService);
  const menuService = inject(MenuService);
  const storeDbService = inject(StoreDbService);

  return checkScreenLock(route, state, router, lock, menuService, storeDbService);
};

// Se ejecuta desde el layout para cubrir todas las entradas visibles del menu,
// incluso cuando Angular reutiliza la misma ruta padre entre dos pantallas.
export const menuScreenLockGuard: CanActivateFn = async (route, state) => {
  const router = inject(Router);
  const menuService = inject(MenuService);
  const lock = inject(ScreenLockService);
  const storeDbService = inject(StoreDbService);

  await menuService.ready();

  const destinationRoute = normalizeRoute(state.url);
  const menuItem = menuService.getMenuByRoute(destinationRoute);

  if (!menuItem || menuItem.visible === false) return true;

  return checkScreenLock(route, state, router, lock, menuService, storeDbService);
};
